import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

export type DropdownMenuItem =
  | {
      label: string;
      icon?: React.FC<{ className?: string; size?: number }>;
      onClick: () => void;
      disabled?: boolean;
      danger?: boolean;
      divider?: false;
    }
  | {
      divider: true;
    };

interface DropdownMenuProps {
  items: DropdownMenuItem[];
  trigger: React.ReactNode;
  align?: 'left' | 'right';
}

const MENU_WIDTH = 192;   // w-48
const MENU_OFFSET = 8;    // mt-2
const VIEWPORT_PAD = 8;   // 不要贴到屏幕边

const DropdownMenu: React.FC<DropdownMenuProps> = ({ items, trigger, align = 'right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 计算菜单出现位置（基于 trigger 在 viewport 中的实测坐标 + 翻转避边）
  const updatePosition = () => {
    const t = triggerRef.current;
    if (!t) return;
    const rect = t.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight ?? 0;

    // 默认右对齐：menu 右边缘 = trigger 右边缘
    let left = align === 'right'
      ? rect.right - MENU_WIDTH
      : rect.left;
    let top = rect.bottom + MENU_OFFSET;

    // 横向避边
    if (left + MENU_WIDTH > window.innerWidth - VIEWPORT_PAD) {
      left = window.innerWidth - MENU_WIDTH - VIEWPORT_PAD;
    }
    if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

    // 下方放不下就翻到上方
    if (menuH > 0 && top + menuH > window.innerHeight - VIEWPORT_PAD) {
      const above = rect.top - MENU_OFFSET - menuH;
      if (above >= VIEWPORT_PAD) top = above;
    }

    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (isOpen) updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const handleReposition = () => updatePosition();

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isOpen]);

  const handleItemClick = (item: DropdownMenuItem) => {
    if ('divider' in item && item.divider) return;
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  return (
    <div ref={triggerRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <div onClick={() => setIsOpen(!isOpen)}>
        {trigger}
      </div>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[1000] w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1" role="menu">
            {items.map((item, index) => (
              <React.Fragment key={index}>
                {'divider' in item && item.divider ? (
                  <div className="my-1 border-t border-gray-200" />
                ) : (
                  <button
                    onClick={() => handleItemClick(item)}
                    disabled={'disabled' in item ? item.disabled : false}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                      'disabled' in item && item.disabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'danger' in item && item.danger
                        ? 'text-red-700 hover:bg-red-50'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    role="menuitem"
                  >
                    {'icon' in item && item.icon && <item.icon size={16} />}
                    <span>{'label' in item ? item.label : ''}</span>
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DropdownMenu;
